import Select from 'react-select';

import { UserDto } from '~modules/user/model';
import calendarIcon from '~shared/assets/icons/calendar.svg';
import searchIcon from '~shared/assets/icons/search.svg';
import { Button } from '~shared/ui/button/button';
import DateIntervalPicker from '~shared/ui/date-interval-picker/date-interval-picker';
import { HeaderFilterPanel } from '~shared/ui/new-table/header-filter-panel/header-filter-panel';

interface UserFilterProps {
	options: UserDto[];
	value: UserDto | undefined;
	inputValue: string;
	loading: boolean;
	onChange: (user: UserDto | undefined) => void;
	onSearchChange: (search: string) => void;
}

function UserFilter({ options, value, inputValue, loading, onChange, onSearchChange }: Readonly<UserFilterProps>) {
	return (
		<div className="header-filter-panel__input-with-icon header-filter-panel__input-with-icon--left">
			<span className="header-filter-panel__input-icon" aria-hidden={true}>
				<img src={searchIcon} alt="" />
			</span>
			<Select<UserDto, false>
				inputId="attendance-user-select"
				className="react-select-container"
				classNamePrefix="react-select"
				options={options}
				value={value}
				inputValue={inputValue}
				getOptionLabel={user => user.name}
				getOptionValue={user => user.id.toString()}
				onChange={user => {
					onChange(user ?? undefined);
					onSearchChange('');
				}}
				onInputChange={(nextInputValue, actionMeta) => {
					if (actionMeta.action === 'input-change') {
						onSearchChange(nextInputValue);
					}
					return nextInputValue;
				}}
				placeholder="Выберите сотрудника..."
				isClearable={true}
				isLoading={loading}
				noOptionsMessage={() => (loading ? 'Поиск...' : 'Нет сотрудников')}
			/>
		</div>
	);
}

interface PeriodFilterProps {
	startDate: Date | null;
	endDate: Date | null;
	onChange: (startDate: Date | null, endDate: Date | null) => void;
}

function PeriodFilter({ startDate, endDate, onChange }: Readonly<PeriodFilterProps>) {
	return (
		<div className="header-filter-panel__input-with-icon header-filter-panel__input-with-icon--right">
			<DateIntervalPicker
				startDate={startDate ?? undefined}
				endDate={endDate ?? undefined}
				onChange={onChange}
				placeholder="Выберите период..."
				className="header-filter-panel__date-picker attendance-date-picker"
			/>
			<span className="header-filter-panel__input-icon" aria-hidden={true}>
				<img src={calendarIcon} alt="" />
			</span>
		</div>
	);
}

interface AttendanceFiltersProps {
	userFilterResetKey: number;
	userOptions: UserDto[];
	selectedUser: UserDto | undefined;
	userSearch: string;
	usersLoading: boolean;
	datePeriod: {
		startDate: Date | null;
		endDate: Date | null;
	};
	onUserChange: (user: UserDto | undefined) => void;
	onUserSearchChange: (search: string) => void;
	onDatePeriodChange: (startDate: Date | null, endDate: Date | null) => void;
	onReset: () => void;
}

export function AttendanceFilters({
	userFilterResetKey,
	userOptions,
	selectedUser,
	userSearch,
	usersLoading,
	datePeriod,
	onUserChange,
	onUserSearchChange,
	onDatePeriodChange,
	onReset,
}: Readonly<AttendanceFiltersProps>) {
	return (
		<HeaderFilterPanel
			actions={
				<Button variant="outline" onClick={onReset}>
					Сбросить
				</Button>
			}
		>
			<div className="header-filter-panel__field">
				<div className="header-filter-panel__label">Поиск по сотрудникам</div>
				<UserFilter
					key={userFilterResetKey}
					options={userOptions}
					value={selectedUser}
					inputValue={userSearch}
					loading={usersLoading}
					onChange={onUserChange}
					onSearchChange={onUserSearchChange}
				/>
			</div>
			<div className="header-filter-panel__field">
				<div className="header-filter-panel__label">Период</div>
				<PeriodFilter
					startDate={datePeriod.startDate}
					endDate={datePeriod.endDate}
					onChange={onDatePeriodChange}
				/>
			</div>
		</HeaderFilterPanel>
	);
}
